import { generateRouteUrl } from 'src/boot/routes';
import { getSideVisitsFromSessionStorage } from 'src/components/pages/eventShow/SideVisitContext';
import type {
  PageFocusedEvent,
  PageName,
  PageUnfocusedEvent,
  PageViewedEvent,
} from 'src/observability/staffEventTypes';

type RouterStateLike = {
  routeName: string;
  params: Record<string, string>;
};

/** The page-event variants this module assembles. */
export type PageEventType = 'page_viewed' | 'page_focused' | 'page_unfocused';

/** Routes whose page_name is fixed regardless of params. */
const DIRECT_ROUTE_PAGE_NAMES: Record<string, PageName> = {
  myDayToday: 'my_day_today',
  patients: 'patients',
  showPatient: 'patients',
  providers: 'providers',
  erx: 'dosespot',
  pharmacyRecommendations: 'pharmacy_recommendations',
};

/**
 * Maps the current router state to the analytics PageName, or undefined when the route is not
 * tracked. The current user's own provider calendar / profile map to my_calendar / profile; another
 * provider's pages are intentionally not tracked.
 */
export const getPageNameFromRouterState = (
  routerState: RouterStateLike,
  authUserId: string | null | undefined,
): PageName | undefined => {
  const { routeName, params } = routerState;

  const directPageName = DIRECT_ROUTE_PAGE_NAMES[routeName];
  if (directPageName) return directPageName;

  const isOwnProviderPage = !!authUserId && params.id === authUserId;
  if (routeName === 'showProvider') return isOwnProviderPage ? 'my_calendar' : undefined;
  if (routeName === 'editProvider') return isOwnProviderPage ? 'profile' : undefined;

  return undefined;
};

/**
 * Builds the split-screen URL (the visit note shown beside a patient page) from the side-visit map
 * in sessionStorage. Returns undefined when there is no patient or no active side visit.
 */
export const getSplitScreenUrl = (patientId: string | undefined): string | undefined => {
  if (!patientId) return undefined;

  const sideEventId = getSideVisitsFromSessionStorage()[patientId];
  if (!sideEventId) return undefined;

  return generateRouteUrl('showPatient', { id: patientId, tab: 'events', tabItem: sideEventId });
};

type BuildPageEventInput = {
  routeName: string;
  params: Record<string, string>;
  authUserId: string | null | undefined;
};

/**
 * Assembles a fully-shaped page event, or undefined when the route is not tracked (caller fires
 * nothing). patient_id/event_id are derived from the URL params, not the MST store: the URL is
 * authoritative and already current, whereas the store does not immediately load the new patient.
 */
export const buildPageEvent = (
  eventType: PageEventType,
  { routeName, params, authUserId }: BuildPageEventInput,
): PageViewedEvent | PageFocusedEvent | PageUnfocusedEvent | undefined => {
  const pageName = getPageNameFromRouterState({ routeName, params }, authUserId);
  if (!pageName) return undefined;

  // patient_id applies only on the patient chart; event_id only on its events tab (:tabItem)
  const patientId = routeName === 'showPatient' ? params.id : undefined;
  const eventId =
    routeName === 'showPatient' && params.tab === 'events' ? params.tabItem : undefined;

  const base = {
    page_name: pageName,
    ...(patientId ? { patient_id: patientId } : {}),
    ...(eventId ? { event_id: eventId } : {}),
  };

  if (eventType === 'page_viewed') {
    // The side pane only renders off the events tab; on the events tab the note is the main content
    // and sessionStorage may still hold an earlier side visit, so skip split_screen_url there.
    const splitScreenUrl = params.tab !== 'events' ? getSplitScreenUrl(patientId) : undefined;
    return {
      event_type: 'page_viewed',
      ...base,
      ...(splitScreenUrl ? { split_screen_url: splitScreenUrl } : {}),
    };
  }
  if (eventType === 'page_focused') {
    return { event_type: 'page_focused', ...base };
  }
  return { event_type: 'page_unfocused', ...base };
};
